import {
    createUnifiedTheme,
    createBaseThemeOptions,
    pageTheme as defaultPageThemes,
    PageTheme,
    palettes,
    UnifiedTheme,
} from '@backstage/theme';
import { AutocompleteClassKey } from '@material-ui/lab/Autocomplete';
import { AlertClassKey } from '@material-ui/lab/Alert';
import { alpha } from '@material-ui/core/styles';

// Labs types not included in overrides
declare module '@material-ui/core/styles/overrides' {
    export interface ComponentNameToClassKey {
        MuiAlert: AlertClassKey;
        MuiAutocomplete: AutocompleteClassKey;
    }
}

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
    pageThemesFontColorOverride[key] = {
        ...defaultPageThemes[key],
        fontColor: '#E4E4E4',
    };
});

export const devTheme: UnifiedTheme = createUnifiedTheme({
    fontFamily: 'JetBrains Mono, monospace',
    pageTheme: pageThemesFontColorOverride,
    defaultPageTheme: 'home',
    ...createBaseThemeOptions({
        palette: {
            ...palettes.dark,
            primary: {
                main: '#61DAFB',
                light: '#88E5FF',
                dark: '#2B95B5',
            },
            secondary: {
                main: '#FF6B6B',
                light: '#FF9999',
                dark: '#CC4444',
            },
            grey: {
                50: '#FFFFFF',
                100: '#E4E4E4',
                200: '#C8C8C8',
                300: '#ACACAC',
                400: '#919191',
                500: '#757575',
                600: '#595959',
                700: '#3D3D3D',
                800: '#212121',
                900: '#141414',
            },
            error: {
                main: '#FF5252',
                light: '#FF8A80',
                dark: '#C41411',
            },
            warning: {
                main: '#FFB74D',
                light: '#FFE97D',
                dark: '#C88719',
            },
            success: {
                main: '#69F0AE',
                light: '#B9F6CA',
                dark: '#00C853',
            },
            info: {
                main: '#64B5F6',
                light: '#90CAF9',
                dark: '#1E88E5',
            },
            navigation: {
                ...palettes.dark.navigation,
                background: '#141414',
                color: '#E4E4E4',
                indicator: '#61DAFB',
                submenu: {
                    background: '#212121'
                },
                navItem: {
                    hoverBackground: alpha('#61DAFB', 0.1),
                },
            },
            text: {
                primary: '#E4E4E4',
            },
            background: {
                default: '#141414',
            },
        }
    }),
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '@global': {
                    div: {
                        '&[class^="BackstageSidebar-drawer-"]': {
                            borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                        },
                        '&[class*="makeStyles-entityTypeBox-"]': {
                            background: 'none',
                            marginLeft: '4px',
                            backgroundColor: '#141414'
                        }
                    },
                    a: {
                        '&[class^="BackstageSidebarSubmenuItem-selected-"]': {
                            backgroundColor: '#FF6B6B'
                        },
                    },
                    hr: {
                        '&[class^="hr-root-"]': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                    }
                }
            }
        },
        BackstageContent: {
            styleOverrides: {
                root: {
                    backgroundColor: '#141414'
                },
            },
        },
        BackstagePage: {
            styleOverrides: {
                root: {
                    borderLeft: '1px solid rgba(255, 255, 255, 0.05)'
                },
            },
        },
        BackstageHeader: {
            styleOverrides: {
                header: {
                    borderBottom: '0px solid rgba(255, 255, 255, 0.1)',
                    backgroundImage: 'unset',
                    boxShadow: 'unset',
                    backgroundColor: '#141414',
                    paddingBottom: '24px',
                    '& button': {
                        color: "#E4E4E4"
                    }
                },
                title: {
                    color: '#E4E4E4'
                },
                subtitle: {
                    color: alpha('#E4E4E4', 0.7),
                },
                breadcrumb: {
                    color: alpha('#E4E4E4', 0.7),
                },
                breadcrumbTitle: {
                    color: alpha('#E4E4E4', 0.7),
                },
                type: {
                    color: alpha('#E4E4E4', 0.7),
                },
            },
        },
        BackstageHeaderLabel: {
            styleOverrides: {
                label: {
                    color: '#E4E4E4',
                },
                value: {
                    color: alpha('#E4E4E4', 0.7),
                },
            },
        },
        BackstageHeaderTabs: {
            styleOverrides: {
                tabRoot: {
                    backgroundColor: '#141414'
                },
                tabsWrapper: {
                    backgroundColor: '#141414'
                },
                defaultTab: {
                    color: alpha('#E4E4E4', 0.7)
                },
                selected: {
                    color: '#61DAFB'
                }
            },
        },
        BackstageSidebarDivider: {
            styleOverrides: {
                root: {
                    color: 'rgba(255, 255, 255, 0.1)',
                    width: '100%',
                }
            },
        },
        BackstageSidebarItem: {
            styleOverrides: {
                root: {
                    textDecoration: 'none',
                    "& h6": {
                        color: '#E4E4E4'
                    },
                },
                closedItemIcon: {
                    color: '#E4E4E4'
                },
                highlighted: {
                    color: '#FF6B6B'
                }
            },
        },
        BackstageItemCardHeader: {
            styleOverrides: {
                root: {
                    backgroundColor: '#212121',
                    backgroundImage: 'unset',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }
            },
        },
        BackstageTable: {
            styleOverrides: {
                root: {
                    '&> :first-child': {
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: 'none',
                    },
                },
            },
        },
        BackstageOpenedDropdown: {
            styleOverrides: {
                icon: {
                    '& path': {
                        fill: '#E4E4E4',
                    },
                },
            },
        },
        BackstageClosedDropdown: {
            styleOverrides: {
                icon: {
                    '& path': {
                        fill: '#E4E4E4',
                    },
                },
            },
        },
        BackstageEmptyState: {
            styleOverrides: {
                action: {
                    '& span[class^="MuiButton-label"]': {
                        color: '#E4E4E4'
                    }
                }
            }
        },
        BackstageAvatar: {
            styleOverrides: {
                avatar: {
                    marginRight: '15px',
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                    backgroundColor: '#3D3D3D'
                }
            }
        },
        BackstageSelect: {
            styleOverrides: {
                root: {
                    marginRight: '15px'
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    "& h3": {
                        color: '#61DAFB'
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    textTransform: 'none'
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    textTransform: 'none'
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    textTransform: 'none'
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    textTransform: 'none'
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    textTransform: 'none'
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    padding: 10,
                },
                switchBase: {
                    padding: 12,
                },
                thumb: {
                    backgroundColor: '#E4E4E4',
                    height: 14,
                    width: 14,
                },
                track: {
                    borderRadius: 9,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#FF6B6B'
                },
            },
        },
        MuiGrid: {
            styleOverrides: {
                root: {
                    borderColor: '1px solid rgba(255, 255, 255, 0.1)'
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: 'unset',
                    backgroundColor: '#212121',
                    marginLeft: '10px'
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    color: '#E4E4E4',
                    backgroundColor: '#3D3D3D',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    marginLeft: '2px'
                },
                outlined: {
                    backgroundColor: '#3D3D3D',
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                }
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(odd)': {
                        backgroundColor: '#212121',
                    },
                    '&:nth-of-type(even)': {
                        backgroundColor: '#1A1A1A',
                    },
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(odd)': {
                        backgroundColor: '#212121',
                    },
                    '&:nth-of-type(even)': {
                        backgroundColor: '#1A1A1A',
                    }
                },
                selected: {
                    "& svg": {
                        color: "#FF6B6B"
                    }
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: '#61DAFB',
                    "& h6": {
                        color: '#61DAFB'
                    }
                },
                underlineHover: {
                    color: '#61DAFB',
                    textDecoration: 'none',
                    outline: 0,
                    "& svg": {
                        color: '#61DAFB'
                    }
                }
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: '15px'
                },
            },
        },
        MuiBottomNavigationAction: {
            styleOverrides: {
                root: {
                    "& svg": {
                        color: '#61DAFB'
                    }
                }
            }
        },
    }
});